import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';
import { useAuthStore } from '../../store/authStore';
import { useAlertManager } from '../../components/AlertProvider';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import './VerifyPhoneScreen.css';

const VerifyPhoneScreen: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlertManager();
  const { user } = useAuthStore();

  const handleVerify = async () => {
    if (!phone || !code) {
      showAlert('Error', 'Please enter phone number and verification code', [{ text: 'OK' }]);
      return;
    }

    setLoading(true);
    try {
      await apiService.post(API_ENDPOINTS.AUTH.VERIFY_PHONE, {
        phone,
        code,
      });
      showAlert('Success', 'Phone verified successfully', [
        {
          text: 'OK',
          onPress: () => {
            if (user?.email_verified_at) {
              navigate('/');
            } else {
              navigate('/verify-email');
            }
          },
        },
      ]);
    } catch (error: any) {
      showAlert('Verification Failed', error.response?.data?.message || 'Invalid code', [{ text: 'OK' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!phone) {
      showAlert('Error', 'Please enter phone number', [{ text: 'OK' }]);
      return;
    }

    try {
      await apiService.post(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        identifier: phone,
        method: 'SMS',
      });
      showAlert('Success', 'Verification code sent', [{ text: 'OK' }]);
    } catch (error: any) {
      showAlert('Error', error.response?.data?.message || 'Failed to resend code', [{ text: 'OK' }]);
    }
  };

  return (
    <div className="verify-phone-container">
      <div className="verify-phone-content">
        <button className="back-button" onClick={() => navigate(-1)}>
          <MaterialCommunityIcons name="arrow-back" size={24} color={Colors.textPrimary} />
        </button>

        <div className="verify-phone-form">
          <div className="icon-container">
            <div className="icon-circle">
              <MaterialCommunityIcons name="phone" size={40} color={Colors.primary} />
            </div>
          </div>

          <h1 className="title">Verify Phone Number</h1>
          <p className="subtitle">Enter the verification code sent to your phone</p>

          <div className="input-wrapper">
            <div className="input-container">
              <MaterialCommunityIcons
                name="phone-outline"
                size={22}
                color={Colors.textSecondary}
              />
              <input
                type="tel"
                className="input-field"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
              />
            </div>
          </div>

          <div className="input-wrapper">
            <div className="input-container">
              <MaterialCommunityIcons
                name="lock-outline"
                size={22}
                color={Colors.textSecondary}
              />
              <input
                type="text"
                className="input-field"
                placeholder="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
              />
            </div>
          </div>

          <button
            className={`verify-button ${loading ? 'disabled' : ''}`}
            onClick={handleVerify}
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify Phone'}
          </button>

          <button className="resend-button" onClick={handleResend}>
            Resend Code
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyPhoneScreen;

